import {Injectable} from '@angular/core';
import {environment} from "../../../../environments/environment";
import {map, merge, retry, Subject, tap, timer} from "rxjs";
import {ConnectionType,} from "./websocket-service.types";
import {TIME_TO_RECONNECT} from "./constants";
import {HttpClient} from "@angular/common/http";
import {take} from "rxjs/operators";
import {WebsocketConnection} from "./websocket-connection";
import {LoggerService} from "../logger.service";
import {LogEvent} from "../../enums/log-event";

@Injectable({
  providedIn: 'root',
})
export class SocketService {

  private _webSockets = {[ConnectionType.LiveChat]: {} as { [key: string]: WebsocketConnection }}
  private reconnect$ = new Subject<void>()

  constructor(
    private httpClient: HttpClient,
    private logger: LoggerService,
  ) {
  }

  connect(reconnectTime = TIME_TO_RECONNECT) {
    const reconnectionTimer = timer(0, reconnectTime);
    return merge(
      reconnectionTimer,
      this.reconnect$
    ).pipe(
      map(() => Object.keys(this._webSockets[ConnectionType.LiveChat]).pop()),
      map((lastConnectionId) => this.createConnection(lastConnectionId)),
      tap((ws) =>
        ws.stopped$.pipe(take(1)).subscribe((reconnect) => {
          if (reconnect) {
            this.logger.log(LogEvent.LIVECHAT_WEBSOCKET_DISCONNECTED, {
              connectionKey: ws.key,
              willReconnect: true,
            });
            this.reconnect$.next();
          }
        })
      )
    );
  }

  public getWebSocketConnection(connectionType: ConnectionType, key: string) {
    return this._webSockets[connectionType][key];
  }

  public sendUpdatedSocketConnection(newConnectionId: string, lastConnectionId: string) {
    return this.httpClient.post<void>(`${environment.externalUrls.bff}/connections/reconnection/${newConnectionId}`, {
      lastConnectionId
    }, {
    }).pipe(
      retry(3)
    )
  }

  finishConnection(connectionType: ConnectionType, key: string) {
    this.getWebSocketConnection(connectionType, key)?.stop(false)
    this.getWebSocketConnection(connectionType, key)?.disconnect()
  }

  clearConnections() {
    this._webSockets[ConnectionType.LiveChat] = {}
  }

  public updateConnectionStatus(connectionId: string, status: string) {
    return this.httpClient.patch<void>(
      `${environment.externalUrls.bff}/connections/${connectionId}/status`,
      { status }
    );
  }

  public storeConnection(connectionType: ConnectionType, key: string, ws: WebsocketConnection) {
    ws.key = key;
    this._webSockets[connectionType][key] = ws
  }

  private createConnection(lastConnectionId?: string) {
    const socketParams = this.getConnectionWebsocketParams()
    const socketUrl = `${environment.externalUrls.bffWebsocket}?${socketParams}`;

    return WebsocketConnection.create(socketUrl, lastConnectionId)
  }

  private getConnectionWebsocketParams() {
    return `system=${ConnectionType.LiveChat}`
  }
}
